import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import styles from "./.module.scss";

const TOUCH_CLAIM_THRESHOLD = 10; // px – movement beyond this decides horizontal vs vertical
const CARD_WIDTH_RATIO = 0.82; // 82% of container width per card

/**
 * Reusable horizontal carousel with touch swipe, RTL support, and bullet indicators.
 * Centers the active slide with peek of prev/next slides when available.
 * First slide: no left peek, Last slide: no right peek.
 * Touch: claims horizontal swipes (preventDefault), leaves vertical scroll to the browser.
 */
function Slideshow({
  children,
  className = "",
  slideClassName = "",
  bulletAriaLabel,
  onVisibleSlideChange,
 slideToShow = 0,
}) {
  const slides = React.Children.toArray(children);
  const count = slides.length;

  const [activeIndex, setActiveIndex] = useState(() => slideToShow || 0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartY = useRef(0);
  const dragOffsetRef = useRef(0);
  const touchClaimedRef = useRef(false);
  const containerRef = useRef(null);

  const goToSlide = useCallback(
    (index) => {
      setActiveIndex(() =>
        Math.max(0, Math.min(index, Math.max(0, count - 1))),
      );
    },
    [count],
  );

  useEffect(() => {
    onVisibleSlideChange?.(activeIndex);
  }, [activeIndex]);

  const handlePointerDown = useCallback((e) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    dragStartX.current = e.clientX;
    dragStartY.current = e.clientY;
    dragOffsetRef.current = 0;
    touchClaimedRef.current = false;
    setIsDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const handlePointerMove = useCallback(
    (e) => {
      if (!isDragging) return;
      const offset = e.clientX - dragStartX.current;
      dragOffsetRef.current = offset;
      setDragOffset(offset);
    },
    [isDragging],
  );

  const handlePointerUp = useCallback(
    (e) => {
      if (!isDragging) return;
      try {
        e.currentTarget?.releasePointerCapture?.(e.pointerId);
      } catch (_) {}
      setIsDragging(false);
      const width = containerRef.current?.offsetWidth ?? 300;
      const threshold = width * 0.2;
      const maxIndex = Math.max(0, count - 1);
      const offset = dragOffsetRef.current;
      if (offset > threshold && activeIndex < maxIndex) {
        goToSlide(activeIndex + 1);
      } else if (offset < -threshold && activeIndex > 0) {
        goToSlide(activeIndex - 1);
      }
      dragOffsetRef.current = 0;
      setDragOffset(0);
    },
    [isDragging, activeIndex, count, goToSlide],
  );

  const handlePointerCancel = useCallback(() => {
    setIsDragging(false);
    dragOffsetRef.current = 0;
    touchClaimedRef.current = false;
    setDragOffset(0);
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    dragStartX.current = t.clientX;
    dragStartY.current = t.clientY;
    dragOffsetRef.current = 0;
    touchClaimedRef.current = false;
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    const deltaX = t.clientX - dragStartX.current;
    const deltaY = t.clientY - dragStartY.current;

    if (touchClaimedRef.current) {
      e.preventDefault();
      const offset = deltaX;
      dragOffsetRef.current = offset;
      setDragOffset(offset);
      return;
    }

    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    if (absX > TOUCH_CLAIM_THRESHOLD || absY > TOUCH_CLAIM_THRESHOLD) {
      if (absX > absY) {
        touchClaimedRef.current = true;
        e.preventDefault();
        dragOffsetRef.current = deltaX;
        setDragOffset(deltaX);
      }
    }
  }, []);

  const handleTouchEnd = useCallback(
    () => {
      if (touchClaimedRef.current) {
        const width = containerRef.current?.offsetWidth ?? 300;
        const threshold = width * 0.2;
        const maxIndex = Math.max(0, count - 1);
        const offset = dragOffsetRef.current;
        if (offset > threshold && activeIndex < maxIndex) {
          goToSlide(activeIndex + 1);
        } else if (offset < -threshold && activeIndex > 0) {
          goToSlide(activeIndex - 1);
        }
      }
      touchClaimedRef.current = false;
      setIsDragging(false);
      dragOffsetRef.current = 0;
      setDragOffset(0);
    },
    [activeIndex, count, goToSlide]
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", handleTouchMove, { passive: false });
  }, [handleTouchMove]);

  // Calculate center offset based on position:
  // - First slide (index 0): 0% offset (card starts at left edge)
  // - Last slide: full peek offset (card ends at right edge)
  // - Middle slides: half peek offset (card centered with equal peek on both sides)
  // Note: CSS transform % is relative to track width, so we convert from container % to track %
  const centerOffsetPercent = useMemo(() => {
    if (count <= 1) return 0;
    const peekSize = (1 - CARD_WIDTH_RATIO) / 2; // 9% of container for 82% card
    const trackWidthRatio = CARD_WIDTH_RATIO * count; // track width as ratio of container
    const maxIndex = count - 1;
    
    if (activeIndex === 0) {
      // First slide: no left offset
      return 0;
    } else if (activeIndex === maxIndex) {
      // Last slide: full offset so card ends at right edge (18% of container)
      return (peekSize * 2 / trackWidthRatio) * 100;
    } else {
      // Middle slides: center the card (9% of container)
      return (peekSize / trackWidthRatio) * 100;
    }
  }, [activeIndex, count]);

  if (count === 0) return null;

  return (
    <div
      ref={containerRef}
      className={`${styles.root} ${className}`.trim()}
      style={{ touchAction: "pan-y" }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <div
        className={`${styles.track} ${isDragging ? styles.isDragging : ""}`.trim()}
        style={{
          "--slide-count": count,
          "--active-index": activeIndex,
          "--drag-offset-px": `${dragOffset}px`,
          "--center-offset-percent": `${centerOffsetPercent}%`,
        }}
      >
        {slides.map((slide, index) => (
          <div
            className={`${styles.slide} ${slideClassName}`.trim()}
            key={slide.key ?? index}
          >
            {slide}
          </div>
        ))}
      </div>
      <div
        className={styles.bullets}
        aria-label={bulletAriaLabel}
        role="tablist"
      >
        {slides.map((_, index) => (
          <button
            key={index}
            type="button"
            role="tab"
            className={styles.bullet}
            aria-label={
              bulletAriaLabel
                ? `${bulletAriaLabel} ${index + 1}`
                : `Slide ${index + 1}`
            }
            aria-selected={index === activeIndex}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </div>
  );
}

export default Slideshow;
